import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMsal } from '@azure/msal-react';
import { createPortalApi } from '../api/portalApi';
import { useRuntimeConfig } from '../context/RuntimeConfigContext';
import {
  parseAiOutput, stripMarkdownFences, validateParseResult,
  type ParseResult, type TcFormat, type ParsedGherkinCase, type ValidationResult,
} from '../utils/aiOutputParser';
import { parseSwaggerSpec, type SwaggerParseResult } from '../utils/swaggerParser';
import { isImageFile, extractImageData, extractTextFromFile, extractImagesFromFile } from '../utils/documentExtractor';
import {
  exportToCsv, exportGherkinFeature,
  exportPlaywrightSpec, exportPostmanCollection,
} from '../utils/exportUtils';
import { runGherkinScenarios, type RunResult } from '../utils/gherkinExecutor';
import { scenariosToPlaywright } from '../utils/playwrightConverter';
import { Icon } from '../components/icons';
import Button from '../components/Button';
import AiThinkingIndicator from '../components/AiThinkingIndicator';
import type { TestCase } from '../types';

// ─── Prompt builders ──────────────────────────────────────────────────────────

function buildJiraPrompt(title: string, desc: string, acceptance: string, other: string, addl: string, types: string[]): string {
  const block = [
    title      ? `JIRA Title: ${title}`              : '',
    desc       ? `Description: ${desc}`              : '',
    acceptance ? `Acceptance Criteria: ${acceptance}`: '',
    other      ? `Other Details: ${other}`           : '',
    types.length ? `Testcase Types: ${types.join(', ')}` : '',
    addl       ? `\nAdditional Information for AI: ${addl}` : '',
  ].filter(Boolean).join('\n');

  return `Context: You are assisting in software manual testing. Create comprehensive test cases covering all relevant scenarios.

Rules:
- Ensure the output STRICTLY adheres to the format below.
- Each test case MUST contain explicit step-by-step actions.
- Generate at least 10 Positive, 15 Negative, and 10 Edge test cases.
- No extra text or explanations. Plain text only — no Markdown formatting (no **, ##, or other decoration).

Input:
${block}

Desired Output:
Test Case ID: TC_POS_01
Title: [specific title]
Test Type: Positive
Preconditions: [preconditions]
Test Steps:
  Step 1. [action]
  Step 2. [action]
Test Data: [data]
Expected Result: [result]

Repeat for every test case.`;
}

function buildDocumentPrompt(documentText: string, additionalInfo: string, types: string[], format: TcFormat): string {
  const typesStr = types.join(', ');
  if (format === 'gherkin') {
    return `Context: You are a senior QA engineer specialising in BDD. Generate comprehensive Gherkin test scenarios based on this document.
Rules: Every Scenario must map to a specific requirement. Use actual field names/values. Each Scenario has a unique ID starting with TC_BDD_. Generate at least 10 Scenarios per type. STRICTLY follow the output format. Plain text only — no Markdown formatting (no **, ##, or other decoration).
Document:\n${documentText}\nAdditional Information:\n${additionalInfo || 'None'}\nRequired Testcase Types: ${typesStr}

Output Format (FOLLOW EXACTLY):
Feature: [Feature name]

Scenario: [TC_BDD_001] [Positive] [Descriptive title]
  Given [precondition]
  When [action]
  And [action]
  Then [expected result]

Repeat Scenario block for every test case. Do NOT repeat the Feature line.`;
  }
  if (format === 'tdd') {
    return `Context: You are a senior QA engineer specialising in data-driven testing. Generate TDD test cases from this document.
Rules: Every test case must reference an exact requirement. Use actual field names/values. Generate at least 10 per type. STRICTLY follow the format below. Plain text only — no Markdown formatting (no **, ##, or other decoration).
Document:\n${documentText}\nAdditional Information:\n${additionalInfo || 'None'}\nRequired Testcase Types: ${typesStr}

Output Format (FOLLOW EXACTLY — blank line between blocks):
Test ID: TC_TDD_001
Test Name: [name]
Requirement: [FR-number and name]
Type: [Positive/Negative/Edge]
Input Data: [field:value pairs]
Steps Summary: [1. step. 2. step.]
Expected Output: [output]
Pass Criteria: [criteria]

Repeat this block for every test case.`;
  }
  return `Context: You are a senior QA engineer. Generate comprehensive manual test cases from this requirement document.
Rules: Every test case must reference a specific requirement. Use actual field names/values. Generate at least 10 per type. Follow the output format exactly. Plain text only — no Markdown formatting (no **, ##, or other decoration).
Document:\n${documentText}\nAdditional Information:\n${additionalInfo || 'None'}\nRequired Testcase Types: ${typesStr}

Output Format:
Test Case ID: TC_001
Title: [title]
Requirement: [requirement]
Test Type: [Positive/Negative/Edge]
Preconditions: [preconditions]
Test Steps:
  Step 1. [action]
Test Data: [data]
Expected Result: [result]

Repeat for every test case.`;
}

function buildImagePrompt(additionalInfo: string, types: string[], format: TcFormat): string {
  const typesStr = types.join(', ');
  if (format === 'gherkin') {
    return `You are a senior QA engineer specialising in BDD. Generate comprehensive Gherkin test scenarios based on this image.
Rules: Every Scenario must map to a specific element visible in the image. Use actual field names/labels. Each has a unique ID starting with TC_BDD_. Generate at least 10 per type. Plain text only — no Markdown formatting (no **, ##, or other decoration).
Additional Information: ${additionalInfo || 'None'}\nRequired Testcase Types: ${typesStr}

Output Format (FOLLOW EXACTLY):
Feature: [Feature name]

Scenario: [TC_BDD_001] [Positive] [Descriptive title]
  Given [precondition]
  When [action]
  And [action]
  Then [expected result]

Repeat Scenario block for every test case. Do NOT repeat the Feature line.`;
  }
  if (format === 'tdd') {
    return `You are a senior QA engineer. Generate TDD test cases based on this image.
Rules: Every test case must reference an element visible in the image. Use actual field names. Generate at least 10 per type.
Additional Information: ${additionalInfo || 'None'}\nRequired Testcase Types: ${typesStr}

Output Format:
Test ID: TC_TDD_001
Test Name: [name]
Requirement: [screen/element]
Type: [Positive/Negative/Edge]
Input Data: [field:value]
Steps Summary: [1. step. 2. step.]
Expected Output: [output]
Pass Criteria: [criteria]

Repeat for every test case.`;
  }
  return `You are a senior QA engineer. Generate comprehensive manual test cases based on this image.
Rules: Every test case must reference a specific element visible in the image. Use actual field names/labels. Generate at least 10 per type.
Additional Information: ${additionalInfo || 'None'}\nRequired Testcase Types: ${typesStr}

Output Format:
Test Case ID: TC_001
Title: [title]
Requirement: [screen/element from image]
Test Type: [Positive/Negative/Edge]
Preconditions: [preconditions]
Test Steps:
  Step 1. [action]
Test Data: [data]
Expected Result: [result]

Repeat for every test case.`;
}

function buildSwaggerPrompt(swaggerSummary: string, additionalInfo: string, types: string[]): string {
  const typesStr = types.join(', ');
  return `You are a senior QA automation engineer specialising in API testing with BDD/Gherkin.
Generate comprehensive Gherkin test scenarios for the following API specification.

API Specification:
${swaggerSummary}

Additional Context: ${additionalInfo || 'None'}
Required Test Case Types: ${typesStr}

Rules:
- Use ONLY these step patterns:
    Given the base URL is "<url>"
    And the request header "<name>" is "<value>"
    When I send a <METHOD> request to "<path>"
    When I send a <METHOD> request to "<path>" with body:
      """
      { "field": "value" }
      """
    Then the response status code should be <code>
    And the response body should contain field "<field>"
    And the response body "<field>" should equal "<value>"
    And the response time should be less than <ms> milliseconds
    And the response header "<name>" should contain "<value>"
- Tag each Scenario with @TC_API_NNN on the line before Scenario:
- Use [Positive], [Negative], or [Edge] in the Scenario title.
- Generate at least 5 scenarios per requested type.
- Output ONLY valid Gherkin — no markdown code fences, no explanations.

Output format (FOLLOW EXACTLY):
Feature: <API name> Test Suite

Background:
  Given the base URL is "<base_url>"
  And the request header "Content-Type" is "application/json"

@TC_API_001
Scenario: [Positive] <scenario title>
  Given the base URL is "<base_url>"
  When I send a GET request to "<path>"
  Then the response status code should be 200
  And the response body should contain field "<field>"

Repeat the @tag + Scenario block for every test case.`;
}

// ─── Parsed result → TestCase[] ──────────────────────────────────────────────

function deduplicateIds(cases: TestCase[]): TestCase[] {
  const seen = new Map<string, number>();
  return cases.map(c => {
    if (!seen.has(c.id)) { seen.set(c.id, 1); return c; }
    const n = seen.get(c.id)! + 1;
    seen.set(c.id, n);
    return { ...c, id: `${c.id}_${n}` };
  });
}

function parsedToTestCases(parsed: ParseResult): TestCase[] {
  let cases: TestCase[] = [];
  if (parsed.format === 'normal' && parsed.normalCases) {
    cases = parsed.normalCases.map(c => ({
      id: c.id, title: c.title, type: c.type,
      preconditions: c.preconditions, steps: c.steps,
      testData: c.testData, expectedResult: c.expectedResult,
      author: '', createdAt: '', status: 'pending', approvals: [], rejections: [],
    }));
  } else if (parsed.format === 'gherkin' && parsed.gherkinCases) {
    cases = parsed.gherkinCases.map(c => ({
      id: c.id, title: c.scenarioName, type: c.type,
      preconditions: '', steps: c.stepLines,
      testData: '', expectedResult: '',
      author: '', createdAt: '', status: 'pending', approvals: [], rejections: [],
    }));
  } else if (parsed.format === 'tdd' && parsed.tddCases) {
    cases = parsed.tddCases.map(c => ({
      id: c.id, title: c.name, type: c.type,
      preconditions: c.requirement, steps: c.stepsSummary,
      testData: c.inputData, expectedResult: c.expectedOutput,
      passCriteria: c.passCriteria,
      author: '', createdAt: '', status: 'pending', approvals: [], rejections: [],
    }));
  }
  return deduplicateIds(cases);
}

// ─── Local types ──────────────────────────────────────────────────────────────

type Mode  = 'jira' | 'manual' | 'document' | 'swagger';
type Phase = 'review' | 'confirmed';

interface JiraFields {
  title: string; description: string; acceptance: string; other: string; additionalInfo: string;
}

interface TcTypeState { positive: boolean; negative: boolean; edge: boolean; }

function selectedTypes(s: TcTypeState): string[] {
  return [s.positive && 'Positive', s.negative && 'Negative', s.edge && 'Edge'].filter(Boolean) as string[];
}

// ─── EditCaseModal ────────────────────────────────────────────────────────────

interface EditModalProps {
  tc: TestCase; format: TcFormat;
  onSave: (updated: TestCase) => void; onClose: () => void;
}

function EditCaseModal({ tc, format, onSave, onClose }: EditModalProps) {
  const [id,            setId]           = useState(tc.id);
  const [title,         setTitle]        = useState(tc.title);
  const [type,          setType]         = useState(tc.type);
  const [preconditions, setPrec]         = useState(tc.preconditions);
  const [stepsText,     setStepsText]    = useState(tc.steps.join('\n'));
  const [testData,      setTestData]     = useState(tc.testData);
  const [expectedResult,setExp]          = useState(tc.expectedResult);
  const [passCriteria,  setPassCriteria] = useState(tc.passCriteria ?? '');

  function handleSave() {
    const steps = stepsText.split('\n').map(s => s.trim()).filter(Boolean);
    onSave({ ...tc, id, title, type, preconditions, steps, testData, expectedResult, passCriteria });
  }

  const Inp = ({ label, value, onChange }: { label: string; value: string; onChange: (v: string) => void }) => (
    <div className="field-wrap"><label className="field-label">{label}</label><input className="form-input" value={value} onChange={e => onChange(e.target.value)} /></div>
  );
  const Ta = ({ label, value, onChange, rows, hint }: { label: string; value: string; onChange: (v: string) => void; rows: number; hint?: string }) => (
    <div className="field-wrap">
      <label className="field-label">{label}{hint && <span className="field-label-hint">{hint}</span>}</label>
      <textarea className="form-input" rows={rows} value={value} onChange={e => onChange(e.target.value)} style={{ resize: 'vertical' }} />
    </div>
  );

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" style={{ width: '100%', maxWidth: 540 }} onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <span className="modal-title">Edit Test Case</span>
          <button className="modal-close" onClick={onClose}><Icon name="x" size={15} /></button>
        </div>
        <div className="modal-body">
          {format === 'gherkin' ? (
            <>
              <Inp label="Scenario ID"   value={id}        onChange={setId} />
              <Inp label="Type"          value={type}      onChange={setType} />
              <Inp label="Scenario Name" value={title}     onChange={setTitle} />
              <Ta  label="Gherkin Steps" value={stepsText} onChange={setStepsText} rows={8} hint="(one per line: Given / When / And / Then)" />
            </>
          ) : format === 'tdd' ? (
            <>
              <Inp label="Test ID"        value={id}            onChange={setId} />
              <Inp label="Test Name"      value={title}         onChange={setTitle} />
              <Inp label="Requirement"    value={preconditions} onChange={setPrec} />
              <Inp label="Type"           value={type}          onChange={setType} />
              <Ta  label="Input Data"     value={testData}      onChange={setTestData}     rows={2} />
              <Ta  label="Steps Summary"  value={stepsText}     onChange={setStepsText}    rows={4} hint="(e.g. Step 1. ...)" />
              <Inp label="Expected Output" value={expectedResult} onChange={setExp} />
              <Inp label="Pass Criteria"   value={passCriteria}  onChange={setPassCriteria} />
            </>
          ) : (
            <>
              <Inp label="Test Case ID"    value={id}            onChange={setId} />
              <Inp label="Title"           value={title}         onChange={setTitle} />
              <Inp label="Type"            value={type}          onChange={setType} />
              <Ta  label="Preconditions"   value={preconditions} onChange={setPrec}     rows={2} />
              <Ta  label="Test Steps"      value={stepsText}     onChange={setStepsText} rows={5} hint="(one per line)" />
              <Inp label="Test Data"       value={testData}      onChange={setTestData} />
              <Inp label="Expected Result" value={expectedResult} onChange={setExp} />
            </>
          )}
        </div>
        <div className="modal-footer">
          <button className="btn btn-outline btn-sm" onClick={onClose}>Cancel</button>
          <button className="btn btn-primary btn-sm" onClick={handleSave}>Save</button>
        </div>
      </div>
    </div>
  );
}

// ─── ConfirmDeleteDialog ──────────────────────────────────────────────────────

interface DeleteDialogProps {
  tcId: string; reason: string;
  onReasonChange: (v: string) => void;
  onConfirm: () => void; onClose: () => void;
}

function ConfirmDeleteDialog({ tcId, reason, onReasonChange, onConfirm, onClose }: DeleteDialogProps) {
  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" style={{ width: '100%', maxWidth: 340 }} onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <span className="modal-title">Delete "{tcId}"?</span>
          <button className="modal-close" onClick={onClose}><Icon name="x" size={15} /></button>
        </div>
        <div className="modal-body">
          <p className="modal-desc">This test case will be permanently removed.</p>
          <div className="form-group">
            <label className="field-label">Reason <span className="field-label-hint">(optional)</span></label>
            <textarea className="form-input" rows={3} value={reason} onChange={e => onReasonChange(e.target.value)}
              placeholder="e.g. Duplicate, out of scope…" style={{ resize: 'vertical' }} />
          </div>
        </div>
        <div className="modal-footer">
          <button className="btn btn-outline btn-sm" onClick={onClose}>Cancel</button>
          <button className="btn btn-danger btn-sm" onClick={onConfirm}>Delete</button>
        </div>
      </div>
    </div>
  );
}

// ─── FieldRow ─────────────────────────────────────────────────────────────────

interface FieldRowProps {
  label: string; value: string; onChange: (v: string) => void;
  multiline?: boolean; placeholder?: string;
}
function FieldRow({ label, value, onChange, multiline, placeholder }: FieldRowProps) {
  return (
    <div className="form-group" style={{ marginBottom: 12 }}>
      <label>{label}</label>
      {multiline
        ? <textarea className="form-input" rows={3} value={value} onChange={e => onChange(e.target.value)} placeholder={placeholder} style={{ resize: 'vertical' }} />
        : <input className="form-input" value={value} onChange={e => onChange(e.target.value)} placeholder={placeholder} />}
    </div>
  );
}

// ─── GherkinSteps ─────────────────────────────────────────────────────────────

const GHERKIN_COLORS: Record<string, string> = {
  Given: 'var(--success)', When: 'var(--accent)', And: 'var(--t2)', Then: 'var(--warn)', But: 'var(--danger)',
};

function GherkinSteps({ steps }: { steps: string[] }) {
  return (
    <>
      {steps.map((line, i) => {
        const m = line.match(/^(Given|When|And|Then|But)\s+(.*)/i);
        if (!m) return <div key={i}>{line}</div>;
        const kw = m[1].charAt(0).toUpperCase() + m[1].slice(1).toLowerCase();
        return <div key={i}><strong style={{ color: GHERKIN_COLORS[kw] || 'var(--t1)' }}>{kw}</strong> {m[2]}</div>;
      })}
    </>
  );
}

// ─── ParseBanner ──────────────────────────────────────────────────────────────

function ParseBanner({ validation, rawText }: { validation: ValidationResult; rawText: string }) {
  if (validation.severity === 'ok') return null;
  const isError = validation.severity === 'error';
  return (
    <div className={`parse-banner parse-banner-${validation.severity}`}>
      <div className="parse-banner-header">
        <span className="parse-banner-icon">{isError ? <Icon name="x" size={13} /> : <Icon name="alert-triangle" size={13} />}</span>
        <strong>{validation.title}</strong>
      </div>
      {validation.detail     && <p className="parse-banner-detail">{validation.detail}</p>}
      {validation.suggestion && <p className="parse-banner-suggestion"><Icon name="lightbulb" size={13} />{validation.suggestion}</p>}
      {rawText && (
        <details className="parse-banner-raw">
          <summary>View raw AI output</summary>
          <pre>{rawText}</pre>
        </details>
      )}
    </div>
  );
}

// ─── PlaywrightPreview ────────────────────────────────────────────────────────

function PlaywrightPreview({ content }: { content: string }) {
  if (!content) return null;
  return (
    <details className="playwright-preview">
      <summary className="playwright-preview-summary">
        Playwright Test Preview (.spec.ts) — {content.split('\n').filter(l => l.trimStart().startsWith("test('")).length} test(s)
      </summary>
      <pre className="playwright-preview-code">{content}</pre>
    </details>
  );
}

// ─── RunResultsPanel ─────────────────────────────────────────────────────────

function RunResultsPanel({ results, running, error }: { results: RunResult[]; running: boolean; error: string }) {
  if (!results.length && !running && !error) return null;
  const passed = results.filter(r => r.status === 'passed').length;
  const failed = results.filter(r => r.status === 'failed').length;
  return (
    <div className="run-results-panel">
      <div className="run-summary">
        <strong>Run Results</strong>
        {results.length > 0 && (
          <>
            <span className="run-summary-total">{results.length} total</span>
            <span className="run-summary-passed">{passed} passed</span>
            {failed > 0 && <span className="run-summary-failed">{failed} failed</span>}
          </>
        )}
        {running && <span className="run-summary-running">Running…</span>}
        {error && <span className="gen-error" style={{ marginLeft: 12 }}>{error}</span>}
      </div>
      {results.map((r, i) => (
        <details key={i} className={`run-result-row run-result-${r.status}`}>
          <summary>
            <span className={`run-status-dot run-dot-${r.status}`} />
            <span className="run-tc-id">{r.id}</span>
            <span className="run-tc-name">{r.name}</span>
            <span className="run-tc-meta">{r.method} {r.url}</span>
            {r.statusCode !== null && <span className="run-tc-code">{r.statusCode}</span>}
            <span className="run-tc-duration">{r.duration}ms</span>
          </summary>
          <div className="run-result-body">
            {r.error && <div className="run-error-msg">{r.error}</div>}
            {r.assertions.map((a, j) => (
              <div key={j} className={`run-assertion-row ${a.passed ? 'run-assert-pass' : 'run-assert-fail'}`}>
                <span className="run-assertion-icon">{a.passed ? <Icon name="check" size={12} /> : <Icon name="x" size={12} />}</span>
                <span className="run-assertion-desc">{a.description}</span>
                {a.actual    && <span className="run-assertion-actual">actual: {a.actual}</span>}
                {!a.passed && a.expected && <span className="run-assertion-expected">expected: {a.expected}</span>}
              </div>
            ))}
          </div>
        </details>
      ))}
    </div>
  );
}

// ─── Main component ───────────────────────────────────────────────────────────

type LlmConfig = { llmProvider?: string; llmModel?: string; llmApiKey?: string; llmAzureEndpoint?: string };

const PROVIDER_LABELS: Record<string, string> = {
  claude: 'Claude', openai: 'OpenAI', groq: 'Groq', azureopenai: 'Azure OpenAI',
};

export default function ApiTestingPage() {
  const { instance } = useMsal();
  const cfg          = useRuntimeConfig();
  const navigate     = useNavigate();

  // ── LLM config (loaded from org settings) ──────────────────────────────────
  const [llmConfig, setLlmConfig] = useState<LlmConfig>({});

  useEffect(() => {
    // 1. Read from localStorage first — the backend GET /settings omits the API key
    //    for security, so LlmSettingsPage writes a browser-side copy on every save.
    const cached = localStorage.getItem('fint_llm_config');
    if (cached) {
      try {
        const parsed = JSON.parse(cached) as LlmConfig;
        if (parsed.llmProvider && parsed.llmApiKey) {
          setLlmConfig(parsed);
          return;
        }
      } catch { /* ignore corrupt cache */ }
    }
    // 2. Fallback: fetch from backend (only works when backend returns the key)
    const tempApi = createPortalApi(instance, cfg.msalClientId, cfg.apiBaseUrl);
    tempApi.getSettings().then(s => {
      if (s?.llmProvider && s.llmApiKey) {
        setLlmConfig({ llmProvider: s.llmProvider, llmModel: s.llmModel, llmApiKey: s.llmApiKey, llmAzureEndpoint: s.llmAzureEndpoint });
      }
    }).catch(() => {});
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const api = createPortalApi(instance, cfg.msalClientId, cfg.apiBaseUrl, llmConfig);

  // ── Mode / Format / Type ───────────────────────────────────────────────────
  const [mode,    setMode]    = useState<Mode>('jira');
  const [format,  setFormat]  = useState<TcFormat>('normal');
  const [tcTypes, setTcTypes] = useState<TcTypeState>({ positive: true, negative: false, edge: false });

  // Effective format: swagger mode always generates Gherkin
  const effectiveFormat: TcFormat = mode === 'swagger' ? 'gherkin' : format;

  // ── JIRA mode ──────────────────────────────────────────────────────────────
  const [jiraId,         setJiraId]         = useState('');
  const [jiraFields,     setJiraFields]     = useState<JiraFields>({ title: '', description: '', acceptance: '', other: '', additionalInfo: '' });
  const [jiraFetching,   setJiraFetching]   = useState(false);
  const [jiraFetchError, setJiraFetchError] = useState('');
  const [jiraFetched,    setJiraFetched]    = useState(false);

  // ── Manual mode ────────────────────────────────────────────────────────────
  const [manualTitle,      setManualTitle]      = useState('');
  const [manualDesc,       setManualDesc]       = useState('');
  const [manualAcceptance, setManualAcceptance] = useState('');
  const [manualAdditional, setManualAdditional] = useState('');

  // ── Document mode ──────────────────────────────────────────────────────────
  const [docFile,       setDocFile]       = useState<File | null>(null);
  const [docAdditional, setDocAdditional] = useState('');
  const fileInputRef = useRef<HTMLInputElement>(null);

  // ── Swagger mode ───────────────────────────────────────────────────────────
  const [swaggerSub,        setSwaggerSub]        = useState<'url' | 'paste'>('url');
  const [swaggerUrl,        setSwaggerUrl]        = useState('');
  const [swaggerJson,       setSwaggerJson]       = useState('');
  const [swaggerData,       setSwaggerData]       = useState<SwaggerParseResult | null>(null);
  const [swaggerFetching,   setSwaggerFetching]   = useState(false);
  const [swaggerError,      setSwaggerError]      = useState('');
  const [swaggerAdditional, setSwaggerAdditional] = useState('');

  // ── Generation state ───────────────────────────────────────────────────────
  const [generating,      setGenerating]      = useState(false);
  const [genStatus,       setGenStatus]       = useState('');
  const [genError,        setGenError]        = useState('');
  const [parsedResult,    setParsedResult]    = useState<ParseResult | null>(null);
  const [allCases,        setAllCases]        = useState<TestCase[]>([]);
  const [selected,        setSelected]        = useState<Set<string>>(new Set());
  const [rawAiText,       setRawAiText]       = useState('');
  const [validation,      setValidation]      = useState<ValidationResult | null>(null);
  const [gherkinCasesRef, setGherkinCasesRef] = useState<ParsedGherkinCase[]>([]);

  // ── Two-phase flow ─────────────────────────────────────────────────────────
  const [phase,          setPhase]          = useState<Phase>('review');
  const [confirmedCases, setConfirmedCases] = useState<TestCase[]>([]);
  const [confirmedPage,  setConfirmedPage]  = useState(1);

  // ── Run tests ──────────────────────────────────────────────────────────────
  const [running,    setRunning]    = useState(false);
  const [runResults, setRunResults] = useState<RunResult[]>([]);
  const [runError,   setRunError]   = useState('');

  // ── Submit ─────────────────────────────────────────────────────────────────
  const [sessionTitle, setSessionTitle] = useState('');
  const [submitting,   setSubmitting]   = useState(false);
  const [submitError,  setSubmitError]  = useState('');

  // ── Pagination (review phase) ──────────────────────────────────────────────
  const PAGE_SIZE  = 5;
  const [page, setPage] = useState(1);
  const totalPages  = Math.ceil(allCases.length / PAGE_SIZE);
  const pageCases   = allCases.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  const confirmedTotalPages = Math.ceil(confirmedCases.length / PAGE_SIZE);
  const confirmedPageCases  = confirmedCases.slice((confirmedPage - 1) * PAGE_SIZE, confirmedPage * PAGE_SIZE);

  // ── Edit / Delete ──────────────────────────────────────────────────────────
  const [editIdx,      setEditIdx]      = useState<number | null>(null);
  const [deleteIdx,    setDeleteIdx]    = useState<number | null>(null);
  const [deleteReason, setDeleteReason] = useState('');

  // ── JIRA fetch ─────────────────────────────────────────────────────────────
  async function fetchJira() {
    if (!jiraId.trim()) return;
    setJiraFetching(true); setJiraFetchError(''); setJiraFetched(false);
    try {
      const data   = await api.fetchJiraIssue(jiraId.trim()) as any;
      const key    = data.key ?? '';
      const fields = data.fields ?? {};
      const summary = fields.summary ?? '';
      const desc    = (fields.description ?? '') as string;

      let mainDesc = desc;
      const acIdx  = desc.search(/acceptance criteria/i);
      const otIdx  = desc.search(/other information/i);
      const cutIdx = acIdx !== -1 && otIdx !== -1 ? Math.min(acIdx, otIdx) : acIdx !== -1 ? acIdx : otIdx;
      if (cutIdx !== -1) mainDesc = desc.substring(0, cutIdx).trim().replace(/h2\..*/i, '').trim();

      let acceptance = 'N/A';
      const acMatch = desc.match(/acceptance criteria\s*([\s\S]*?)(?:\n\s*other information|$)/i);
      if (acMatch?.[1]) acceptance = acMatch[1].replace(/h2\..*/i, '').replace(/\n{2,}/g, '\n').replace(/\n?N\/A\s*$/i, '').trim();

      let other = 'N/A';
      const otMatch = desc.match(/other information\s*([\s\S]*?)(?:$|\n)/i);
      if (otMatch?.[1]) other = otMatch[1].trim();

      setJiraFields({ title: `${key} - ${summary}`, description: mainDesc, acceptance, other, additionalInfo: '' });
      setJiraFetched(true);
    } catch (e: any) {
      setJiraFetchError(e.message || 'Failed to fetch JIRA issue');
    } finally {
      setJiraFetching(false);
    }
  }

  // ── Swagger: fetch from URL ────────────────────────────────────────────────
  async function fetchSwaggerFromUrl() {
    if (!swaggerUrl.trim()) return;
    setSwaggerFetching(true); setSwaggerError(''); setSwaggerData(null);
    try {
      const response = await fetch(swaggerUrl.trim());
      if (!response.ok) throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      const text = await response.text();
      const data = parseSwaggerSpec(text);
      setSwaggerData(data);
    } catch (e: any) {
      const msg = e.message || '';
      if (msg.includes('Failed to fetch') || msg.includes('NetworkError') || msg.includes('CORS')) {
        setSwaggerError(`Could not fetch spec (CORS blocked). Use "Paste JSON" mode — copy the raw JSON from your browser's network tab and paste it here.`);
      } else {
        setSwaggerError(msg);
      }
    } finally {
      setSwaggerFetching(false);
    }
  }

  // ── Swagger: parse pasted JSON ─────────────────────────────────────────────
  function parseSwaggerPaste() {
    setSwaggerError(''); setSwaggerData(null);
    try {
      const data = parseSwaggerSpec(swaggerJson.trim());
      setSwaggerData(data);
    } catch (e: any) {
      setSwaggerError(e.message || 'Invalid JSON or unrecognised spec format.');
    }
  }

  // ── Generate ───────────────────────────────────────────────────────────────
  async function generate() {
    const types = selectedTypes(tcTypes);
    if (!types.length) { setGenError('Select at least one test case type.'); return; }

    setGenerating(true);
    setGenError('');
    setParsedResult(null);
    setAllCases([]);
    setSelected(new Set());
    setPage(1);
    setRawAiText('');
    setValidation(null);
    setGherkinCasesRef([]);
    setPhase('review');
    setConfirmedCases([]);
    setRunResults([]);
    setRunError('');

    try {
      let aiText: string;

      if (mode === 'jira' || mode === 'manual') {
        const title = mode === 'jira' ? jiraFields.title       : manualTitle;
        const desc  = mode === 'jira' ? jiraFields.description : manualDesc;
        const acc   = mode === 'jira' ? jiraFields.acceptance  : manualAcceptance;
        const other = mode === 'jira' ? jiraFields.other       : '';
        const addl  = mode === 'jira' ? jiraFields.additionalInfo : manualAdditional;
        setGenStatus('Generating test cases…');
        aiText = (await api.callAI(buildJiraPrompt(title, desc, acc, other, addl, types), types)).text;

      } else if (mode === 'swagger') {
        if (!swaggerData) { setGenError('Load a Swagger/OpenAPI spec first.'); setGenerating(false); return; }
        setGenStatus('Generating API test scenarios…');
        aiText = (await api.callAI(buildSwaggerPrompt(swaggerData.text, swaggerAdditional, types))).text;

      } else {
        // document mode
        if (!docFile) { setGenError('Please upload a file first.'); setGenerating(false); return; }
        if (isImageFile(docFile)) {
          setGenStatus('Reading image…');
          const { base64, mediaType } = await extractImageData(docFile);
          setGenStatus('Analyzing image…');
          aiText = (await api.callAIWithContent(buildImagePrompt(docAdditional, types, format), [{ base64, mediaType }], types)).text;
        } else {
          setGenStatus('Checking for embedded images…');
          const embedded = await extractImagesFromFile(docFile);
          if (embedded.length > 0) {
            setGenStatus('Analyzing embedded image…');
            aiText = (await api.callAIWithContent(buildImagePrompt(docAdditional, types, format), [{ base64: embedded[0].base64, mediaType: embedded[0].mediaType }], types)).text;
          } else {
            setGenStatus('Extracting document text…');
            const text = await extractTextFromFile(docFile);
            setGenStatus('Generating test cases…');
            aiText = (await api.callAI(buildDocumentPrompt(text, docAdditional, types, format), types)).text;
          }
        }
      }

      // Strip markdown fences (LLMs often wrap Gherkin in ```)
      const stripped = stripMarkdownFences(aiText);
      setRawAiText(stripped);

      // Parse
      const parsed = parseAiOutput(stripped, effectiveFormat);

      // Validate and surface quality warnings
      const v = validateParseResult(stripped, parsed, types);
      if (v.severity !== 'ok') setValidation(v);

      if (!parsed) {
        setGenError('Could not parse AI output. Check format or try again.');
        return;
      }

      const cases = parsedToTestCases(parsed);
      setParsedResult(parsed);
      setAllCases(cases);
      setSelected(new Set(cases.map(c => c.id)));

      // Keep raw Gherkin cases for Playwright/Postman converters.
      // Apply the same ID deduplication so IDs match confirmedCases.
      if (effectiveFormat === 'gherkin' && parsed.gherkinCases) {
        const seen = new Map<string, number>();
        const deduped = parsed.gherkinCases.map(gc => {
          if (!seen.has(gc.id)) { seen.set(gc.id, 1); return gc; }
          const n = seen.get(gc.id)! + 1;
          seen.set(gc.id, n);
          return { ...gc, id: `${gc.id}_${n}` };
        });
        setGherkinCasesRef(deduped);
      }

      setSessionTitle(
        mode === 'jira'    ? jiraFields.title :
        mode === 'manual'  ? manualTitle :
        mode === 'swagger' ? (swaggerData?.title || 'API Tests') :
        docFile?.name ?? 'Generated'
      );
    } catch (e: any) {
      setGenError(e.message || 'Generation failed.');
    } finally {
      setGenerating(false); setGenStatus('');
    }
  }

  // ── Submit to portal ───────────────────────────────────────────────────────
  async function submitToPortal() {
    const toSubmit = phase === 'confirmed' ? confirmedCases : allCases.filter(c => selected.has(c.id));
    if (!toSubmit.length) { setSubmitError('Select at least one test case.'); return; }
    if (!sessionTitle.trim()) { setSubmitError('Enter a session title.'); return; }
    setSubmitting(true); setSubmitError('');
    try {
      const { sessionId } = await api.createSession({ title: sessionTitle.trim(), outputType: effectiveFormat, cases: toSubmit });
      navigate(`/sessions/${sessionId}`);
    } catch (e: any) {
      setSubmitError(e.message || 'Submission failed.');
    } finally {
      setSubmitting(false);
    }
  }

  // ── Selection ──────────────────────────────────────────────────────────────
  function toggleSelect(id: string) {
    setSelected(prev => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n; });
  }
  function toggleAll() {
    setSelected(prev => prev.size === allCases.length ? new Set() : new Set(allCases.map(c => c.id)));
  }

  function togglePage() {
    const allPageSelected = pageCases.every(c => selected.has(c.id));
    setSelected(prev => {
      const n = new Set(prev);
      if (allPageSelected) {
        pageCases.forEach(c => n.delete(c.id));
      } else {
        pageCases.forEach(c => n.add(c.id));
      }
      return n;
    });
  }

  // ── Edit ───────────────────────────────────────────────────────────────────
  function handleEditSave(updated: TestCase) {
    setAllCases(prev => prev.map((tc, i) => i === editIdx ? updated : tc));
    setEditIdx(null);
  }

  // ── Delete ─────────────────────────────────────────────────────────────────
  function confirmDelete() {
    if (deleteIdx === null) return;
    const deletedId = allCases[deleteIdx].id;
    const newCases  = allCases.filter((_, i) => i !== deleteIdx);
    setAllCases(newCases);
    setSelected(prev => { const n = new Set(prev); n.delete(deletedId); return n; });
    const newTotalPages = Math.max(1, Math.ceil(newCases.length / PAGE_SIZE));
    if (page > newTotalPages) setPage(newTotalPages);
    setDeleteIdx(null);
    setDeleteReason('');
  }

  // ── Two-phase: Confirm / Back ──────────────────────────────────────────────
  function confirmSelection() {
    const cases = allCases.filter(c => selected.has(c.id));
    if (!cases.length) { setGenError('Select at least one test case to confirm.'); return; }
    setConfirmedCases(cases);
    setConfirmedPage(1);
    setPhase('confirmed');
    setRunResults([]);
    setRunError('');
  }

  function backToReview() {
    setPhase('review');
    setRunResults([]);
    setRunError('');
  }

  // ── Exports ────────────────────────────────────────────────────────────────
  function handleExport() {
    const toExport = selected.size > 0 ? allCases.filter(c => selected.has(c.id)) : allCases;
    const title    = sessionTitle || 'test-cases';
    if (effectiveFormat === 'gherkin') {
      exportGherkinFeature(toExport, parsedResult?.featureName ?? '', title);
    } else {
      exportToCsv(toExport, effectiveFormat, title);
    }
  }

  function getConfirmedGherkinCases(): ParsedGherkinCase[] {
    return gherkinCasesRef.filter(gc => confirmedCases.some(cc => cc.id === gc.id));
  }

  function handlePlaywrightExport() {
    const cases = getConfirmedGherkinCases();
    if (!cases.length) return;
    exportPlaywrightSpec(cases, rawAiText, parsedResult?.featureName ?? sessionTitle, sessionTitle);
  }

  function handlePostmanExport() {
    const cases = getConfirmedGherkinCases();
    if (!cases.length) return;
    exportPostmanCollection(cases, rawAiText, parsedResult?.featureName ?? sessionTitle, sessionTitle);
  }

  function handleConfirmedExport() {
    const title = sessionTitle || 'test-cases';
    if (effectiveFormat === 'gherkin') {
      exportGherkinFeature(confirmedCases, parsedResult?.featureName ?? '', title);
    } else {
      exportToCsv(confirmedCases, effectiveFormat, title);
    }
  }

  // ── Run API tests ──────────────────────────────────────────────────────────
  async function runTests() {
    const cases = getConfirmedGherkinCases();
    if (!cases.length) { setRunError('No Gherkin cases to run.'); return; }
    setRunning(true); setRunError(''); setRunResults([]);
    try {
      await runGherkinScenarios(cases, rawAiText, r => {
        setRunResults(prev => [...prev, r]);
      });
    } catch (e: any) {
      setRunError(e.message || 'Run failed.');
    } finally {
      setRunning(false);
    }
  }

  // ── Playwright preview content ─────────────────────────────────────────────
  const playwrightPreview = useMemo<string>(() => {
    if (phase !== 'confirmed' || effectiveFormat !== 'gherkin') return '';
    const cases = gherkinCasesRef.filter(gc => confirmedCases.some(cc => cc.id === gc.id));
    if (!cases.length) return '';
    try { return scenariosToPlaywright(cases, rawAiText, parsedResult?.featureName ?? sessionTitle); }
    catch { return ''; }
  }, [phase, effectiveFormat, confirmedCases, gherkinCasesRef, rawAiText, parsedResult, sessionTitle]);

  // ── canGenerate ────────────────────────────────────────────────────────────
  const canGenerate =
    mode === 'swagger'  ? !!swaggerData :
    mode === 'jira'     ? jiraFetched && !!jiraFields.title && !!jiraFields.description :
    mode === 'manual'   ? !!manualTitle.trim() && !!manualDesc.trim() && !!manualAcceptance.trim() :
    !!docFile;

  // ── Render ─────────────────────────────────────────────────────────────────
  return (
    <div className="page generator-page">

      {/* Modals */}
      {editIdx !== null && (
        <EditCaseModal tc={allCases[editIdx]} format={effectiveFormat} onSave={handleEditSave} onClose={() => setEditIdx(null)} />
      )}
      {deleteIdx !== null && (
        <ConfirmDeleteDialog
          tcId={allCases[deleteIdx]?.id ?? ''} reason={deleteReason} onReasonChange={setDeleteReason}
          onConfirm={confirmDelete} onClose={() => { setDeleteIdx(null); setDeleteReason(''); }}
        />
      )}

      {/* Page header */}
      <div className="page-header">
        <div>
          <h1 className="page-title">API Testing</h1>
          <p className="page-subtitle">Generate AI test cases from JIRA stories, manual input, documents, or Swagger/OpenAPI specs.</p>
        </div>
        <span className="gen-provider-badge" title={llmConfig.llmModel || ''}>
          AI: {llmConfig.llmProvider && llmConfig.llmApiKey
            ? (PROVIDER_LABELS[llmConfig.llmProvider] || llmConfig.llmProvider)
            : 'Claude (managed)'}
        </span>
      </div>

      {/* Controls bar */}
      <div className="gen-controls-bar">
        <div className="gen-control-group">
          <span className="gen-control-label">Source</span>
          <div className="gen-toggle-row">
            {(['jira', 'manual', 'document', 'swagger'] as Mode[]).map(m => (
              <button key={m}
                className={`gen-toggle-btn ${mode === m ? 'active' : ''}`}
                onClick={() => { setMode(m); setGenError(''); setParsedResult(null); setAllCases([]); setValidation(null); setPhase('review'); }}>
                {m === 'jira' ? 'JIRA' : m === 'manual' ? 'Manual' : m === 'document' ? 'Document' : 'Swagger'}
              </button>
            ))}
          </div>
        </div>

        <div className="gen-control-group">
          <span className="gen-control-label">Format</span>
          {mode === 'swagger' ? (
            <div className="gen-toggle-row">
              <span className="gen-toggle-btn active" style={{ cursor: 'default', opacity: 0.85 }}>Gherkin (API)</span>
            </div>
          ) : (
            <div className="gen-toggle-row">
              {(['normal', 'gherkin', 'tdd'] as TcFormat[]).map(f => (
                <button key={f}
                  className={`gen-toggle-btn ${format === f ? 'active' : ''}`}
                  onClick={() => setFormat(f)}>
                  {f === 'normal' ? 'Normal' : f === 'gherkin' ? 'Gherkin (BDD)' : 'Excel (TDD)'}
                </button>
              ))}
            </div>
          )}
        </div>

        <div className="gen-control-group">
          <span className="gen-control-label">Test Type</span>
          <div className="gen-toggle-row">
            {(['positive', 'negative', 'edge'] as const).map(t => (
              <label key={t} className={`gen-check-label ${tcTypes[t] ? 'active' : ''}`}>
                <input type="checkbox" checked={tcTypes[t]} onChange={() => setTcTypes(s => ({ ...s, [t]: !s[t] }))} />
                {t.charAt(0).toUpperCase() + t.slice(1)}
              </label>
            ))}
          </div>
        </div>
      </div>

      {/* Input panel */}
      <div className="gen-input-panel">

        {/* JIRA */}
        {mode === 'jira' && (
          <div className="gen-card">
            <h2 className="gen-section-title">JIRA Issue</h2>
            <div className="gen-row">
              <input className="form-input gen-jira-input" placeholder="e.g. SCRUM-123" value={jiraId}
                onChange={e => { setJiraId(e.target.value); setJiraFetched(false); }}
                onKeyDown={e => e.key === 'Enter' && fetchJira()} />
              <button className="btn btn-outline btn-sm" onClick={fetchJira} disabled={jiraFetching || !jiraId.trim()}>
                {jiraFetching ? 'Fetching…' : 'Fetch Story'}
              </button>
            </div>
            {jiraFetchError && <div className="gen-error">{jiraFetchError}</div>}
            {jiraFetched && (
              <div className="gen-jira-fields">
                <FieldRow label="Title"                  value={jiraFields.title}          onChange={v => setJiraFields(f => ({ ...f, title: v }))} />
                <FieldRow label="Description"            value={jiraFields.description}    onChange={v => setJiraFields(f => ({ ...f, description: v }))}    multiline />
                <FieldRow label="Acceptance Criteria"    value={jiraFields.acceptance}     onChange={v => setJiraFields(f => ({ ...f, acceptance: v }))}     multiline />
                <FieldRow label="Other Details"          value={jiraFields.other}          onChange={v => setJiraFields(f => ({ ...f, other: v }))}          multiline />
                <FieldRow label="Additional Info for AI" value={jiraFields.additionalInfo} onChange={v => setJiraFields(f => ({ ...f, additionalInfo: v }))} multiline placeholder="Optional: sample steps, focus areas…" />
              </div>
            )}
          </div>
        )}

        {/* Manual */}
        {mode === 'manual' && (
          <div className="gen-card">
            <h2 className="gen-section-title">Manual Input</h2>
            <FieldRow label="Title *"                value={manualTitle}      onChange={setManualTitle}      placeholder="Feature or user story title" />
            <FieldRow label="Description *"          value={manualDesc}       onChange={setManualDesc}       multiline placeholder="Describe the feature or user story" />
            <FieldRow label="Acceptance Criteria *"  value={manualAcceptance} onChange={setManualAcceptance} multiline placeholder="Acceptance criteria" />
            <FieldRow label="Additional Info for AI" value={manualAdditional} onChange={setManualAdditional} multiline placeholder="Optional: sample steps, context…" />
          </div>
        )}

        {/* Document */}
        {mode === 'document' && (
          <div className="gen-card">
            <h2 className="gen-section-title">Upload Document or Image</h2>
            <label
              className={`gen-dropzone ${docFile ? 'has-file' : ''}`}
              onDragOver={e => { e.preventDefault(); e.currentTarget.classList.add('drag-over'); }}
              onDragLeave={e => e.currentTarget.classList.remove('drag-over')}
              onDrop={e => {
                e.preventDefault(); e.currentTarget.classList.remove('drag-over');
                const f = e.dataTransfer.files[0]; if (f) setDocFile(f);
              }}
            >
              <span className="gen-dropzone-icon"><Icon name="document" size={26} /></span>
              <span className="gen-dropzone-text">{docFile ? docFile.name : 'Click to upload or drag & drop'}</span>
              <span className="gen-dropzone-hint">PDF · Word (.docx) · Excel (.xlsx) · TXT · CSV · PNG · JPG · GIF · WEBP</span>
              <input ref={fileInputRef} type="file"
                accept=".pdf,.docx,.xlsx,.xls,.txt,.csv,.md,.png,.jpg,.jpeg,.gif,.webp,.bmp"
                style={{ display: 'none' }}
                onChange={e => { if (e.target.files?.[0]) setDocFile(e.target.files[0]); }}
              />
            </label>
            {docFile && (
              <button className="btn btn-outline btn-sm" style={{ marginTop: 8 }}
                onClick={() => { setDocFile(null); if (fileInputRef.current) fileInputRef.current.value = ''; }}>
                Remove file
              </button>
            )}
            <FieldRow label="Additional Info for AI" value={docAdditional} onChange={setDocAdditional} multiline placeholder="Optional: focus areas, context…" />
          </div>
        )}

        {/* Swagger */}
        {mode === 'swagger' && (
          <div className="gen-card">
            <h2 className="gen-section-title">Swagger / OpenAPI Specification</h2>
            <div className="swagger-sub-tabs">
              <button className={`swagger-sub-btn ${swaggerSub === 'url'   ? 'active' : ''}`} onClick={() => { setSwaggerSub('url');   setSwaggerError(''); }}>URL</button>
              <button className={`swagger-sub-btn ${swaggerSub === 'paste' ? 'active' : ''}`} onClick={() => { setSwaggerSub('paste'); setSwaggerError(''); }}>Paste JSON</button>
            </div>

            {swaggerSub === 'url' && (
              <div>
                <p className="swagger-hint">
                  Fetch the raw OpenAPI JSON from a public URL. The API must have CORS enabled — if it's blocked, use Paste JSON instead.
                </p>
                <div className="gen-row">
                  <input className="form-input" placeholder="https://api.example.com/openapi.json"
                    value={swaggerUrl}
                    onChange={e => { setSwaggerUrl(e.target.value); setSwaggerData(null); setSwaggerError(''); }} />
                  <button className="btn btn-outline btn-sm" onClick={fetchSwaggerFromUrl} disabled={swaggerFetching || !swaggerUrl.trim()}>
                    {swaggerFetching ? 'Fetching…' : 'Fetch Spec'}
                  </button>
                </div>
                {swaggerError && <div className="gen-error" style={{ marginTop: 6 }}>{swaggerError}</div>}
              </div>
            )}

            {swaggerSub === 'paste' && (
              <div>
                <p className="swagger-hint">
                  Paste your OpenAPI 3.x or Swagger 2.0 JSON below.
                </p>
                <textarea className="form-input swagger-json-input" rows={8}
                  value={swaggerJson}
                  onChange={e => { setSwaggerJson(e.target.value); setSwaggerData(null); setSwaggerError(''); }}
                  placeholder='{ "openapi": "3.0.0", "info": { ... }, "paths": { ... } }'
                />
                <div style={{ marginTop: 6, display: 'flex', gap: 8, alignItems: 'center' }}>
                  <button className="btn btn-outline btn-sm" onClick={parseSwaggerPaste} disabled={!swaggerJson.trim()}>Parse Spec</button>
                  {swaggerError && <span className="gen-error">{swaggerError}</span>}
                </div>
              </div>
            )}

            {swaggerData && (
              <div className="swagger-summary-box">
                <div className="swagger-summary-title">{swaggerData.title}</div>
                <div className="swagger-summary-meta">
                  {swaggerData.endpointCount} endpoint{swaggerData.endpointCount !== 1 ? 's' : ''}
                  {swaggerData.baseUrl ? ` · ${swaggerData.baseUrl}` : ''}
                </div>
              </div>
            )}

            <div style={{ marginTop: 12 }}>
              <FieldRow label="Additional Context" value={swaggerAdditional} onChange={setSwaggerAdditional}
                multiline placeholder="Optional: focus areas, auth details, priority endpoints…" />
            </div>
          </div>
        )}

        {/* Generate button */}
        <div style={{ display: 'flex', gap: 12, alignItems: 'center', marginTop: 4 }}>
          <Button variant="ai" isGenerating={generating} onClick={generate} disabled={generating || !canGenerate}>
            {generating ? <AiThinkingIndicator status={genStatus} fallback="Generating…" /> : <><Icon name="sparkle" size={14} /> Generate Test Cases with AI</>}
          </Button>
          {genError && <span className="gen-error">{genError}</span>}
        </div>
      </div>

      {/* Parse quality banner (shows after generation if there's a warning/error) */}
      {validation && validation.severity !== 'ok' && (
        <ParseBanner validation={validation} rawText={rawAiText} />
      )}

      {/* Results section */}
      {allCases.length > 0 && (
        <div className="gen-results">

          {/* ── REVIEW PHASE ── */}
          {phase === 'review' && (
            <>
              <div className="gen-results-header">
                <div>
                  <h2 className="gen-section-title" style={{ marginBottom: 2 }}>
                    Review Generated Cases
                    <span className="gen-count-badge">{allCases.length} cases</span>
                  </h2>
                  {parsedResult?.featureName && (
                    <div className="gen-feature-name">Feature: {parsedResult.featureName}</div>
                  )}
                </div>
                <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                  <button className="btn btn-outline btn-sm" onClick={toggleAll}>
                    {allCases.length > 0 && selected.size === allCases.length ? 'Unselect All' : 'Select All'}
                  </button>
                  <button className="btn btn-outline btn-sm" onClick={handleExport}>
                    {effectiveFormat === 'gherkin' ? 'Export .feature' : effectiveFormat === 'tdd' ? 'Export TDD CSV' : 'Export CSV'}
                  </button>
                  <span className="gen-selected-count">{selected.size} of {allCases.length} selected</span>
                  <button className="btn btn-primary btn-sm" onClick={confirmSelection} disabled={selected.size === 0}>
                    Confirm Selection ({selected.size})
                  </button>
                </div>
              </div>

              {/* Table */}
              <div className="gen-table-wrapper">
                <table className="gen-table">
                  <thead>
                    <tr>
                      <th style={{ width: 36 }}>
                        <input type="checkbox" checked={pageCases.length > 0 && pageCases.every(c => selected.has(c.id))} onChange={togglePage} />
                      </th>
                      {effectiveFormat === 'normal'  && <><th>ID</th><th>Title</th><th>Type</th><th>Preconditions</th><th>Steps</th><th>Test Data</th><th>Expected Result</th></>}
                      {effectiveFormat === 'gherkin' && <><th>ID</th><th>Type</th><th>Scenario Name</th><th>Gherkin Steps</th></>}
                      {effectiveFormat === 'tdd'     && <><th>ID</th><th>Name</th><th>Requirement</th><th>Type</th><th>Input Data</th><th>Steps Summary</th><th>Expected Output</th><th>Pass Criteria</th></>}
                      <th className="gen-actions-th">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {pageCases.map((tc, pageIdx) => {
                      const globalIdx = (page - 1) * PAGE_SIZE + pageIdx;
                      return (
                        <tr key={tc.id} className={selected.has(tc.id) ? 'gen-row-selected' : ''}>
                          <td><input type="checkbox" checked={selected.has(tc.id)} onChange={() => toggleSelect(tc.id)} /></td>

                          {effectiveFormat === 'normal' && <>
                            <td className="gen-id-cell">{tc.id}</td>
                            <td>{tc.title}</td>
                            <td><span className={`type-badge type-${(tc.type || '').toLowerCase()}`}>{tc.type}</span></td>
                            <td className="gen-cell-wrap">{tc.preconditions}</td>
                            <td className="gen-cell-wrap"><ol className="steps-list">{tc.steps.map((s, i) => <li key={i}>{s}</li>)}</ol></td>
                            <td className="gen-cell-wrap">{tc.testData}</td>
                            <td className="gen-cell-wrap">{tc.expectedResult}</td>
                          </>}

                          {effectiveFormat === 'gherkin' && <>
                            <td className="gen-id-cell">{tc.id}</td>
                            <td><span className={`type-badge type-${(tc.type || '').toLowerCase()}`}>{tc.type}</span></td>
                            <td>{tc.title}</td>
                            <td className="gen-cell-wrap gen-gherkin-steps"><GherkinSteps steps={tc.steps} /></td>
                          </>}

                          {effectiveFormat === 'tdd' && <>
                            <td className="gen-id-cell">{tc.id}</td>
                            <td>{tc.title}</td>
                            <td className="gen-cell-wrap">{tc.preconditions}</td>
                            <td><span className={`type-badge type-${(tc.type || '').toLowerCase()}`}>{tc.type}</span></td>
                            <td className="gen-cell-wrap">{tc.testData}</td>
                            <td className="gen-cell-wrap"><ol className="steps-list">{tc.steps.map((s, i) => <li key={i}>{s}</li>)}</ol></td>
                            <td className="gen-cell-wrap">{tc.expectedResult}</td>
                            <td className="gen-cell-wrap">{tc.passCriteria ?? ''}</td>
                          </>}

                          <td className="gen-actions-td">
                            <button className="gen-action-btn gen-edit-btn" title="Edit" onClick={() => setEditIdx(globalIdx)}><Icon name="edit" size={13} /></button>
                            <button className="gen-action-btn gen-del-btn"  title="Delete" onClick={() => { setDeleteIdx(globalIdx); setDeleteReason(''); }}><Icon name="trash" size={13} /></button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              {totalPages > 1 && (
                <div className="gen-pagination">
                  <button className="btn btn-outline btn-sm" onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1}>← Prev</button>
                  <span className="gen-page-info">Page {page} of {totalPages}</span>
                  <button className="btn btn-outline btn-sm" onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page === totalPages}>Next →</button>
                </div>
              )}
            </>
          )}

          {/* ── CONFIRMED PHASE ── */}
          {phase === 'confirmed' && (
            <>
              <div className="gen-results-header">
                <div>
                  <h2 className="gen-section-title" style={{ marginBottom: 2 }}>
                    Confirmed Test Cases
                    <span className="gen-confirmed-badge">{confirmedCases.length} confirmed</span>
                  </h2>
                  {parsedResult?.featureName && (
                    <div className="gen-feature-name">Feature: {parsedResult.featureName}</div>
                  )}
                </div>
                <button className="btn btn-outline btn-sm" onClick={backToReview}>← Edit Selection</button>
              </div>

              {/* Playwright preview (Gherkin only) */}
              {effectiveFormat === 'gherkin' && playwrightPreview && (
                <div style={{ padding: '0 18px' }}>
                  <PlaywrightPreview content={playwrightPreview} />
                </div>
              )}

              {/* Export buttons */}
              <div className="gen-confirmed-actions">
                <button className="btn btn-outline btn-sm" onClick={handleConfirmedExport}>
                  {effectiveFormat === 'gherkin' ? 'Export .feature' : effectiveFormat === 'tdd' ? 'Export TDD CSV' : 'Export CSV'}
                </button>
                {effectiveFormat === 'gherkin' && (
                  <>
                    <button className="btn btn-outline btn-sm" onClick={handlePlaywrightExport}>Export Playwright .spec.ts</button>
                    <button className="btn btn-outline btn-sm" onClick={handlePostmanExport}>Export Postman Collection</button>
                    <button className="btn btn-outline btn-sm" onClick={runTests} disabled={running}>
                      {running ? 'Running…' : '▶ Run API Tests'}
                    </button>
                  </>
                )}
              </div>

              {/* Run results */}
              <div style={{ padding: '0 18px' }}>
                <RunResultsPanel results={runResults} running={running} error={runError} />
              </div>

              {/* Confirmed cases table (read-only) */}
              <div className="gen-table-wrapper">
                <table className="gen-table">
                  <thead>
                    <tr>
                      {effectiveFormat === 'normal'  && <><th>ID</th><th>Title</th><th>Type</th><th>Preconditions</th><th>Steps</th><th>Test Data</th><th>Expected Result</th></>}
                      {effectiveFormat === 'gherkin' && <><th>ID</th><th>Type</th><th>Scenario Name</th><th>Gherkin Steps</th></>}
                      {effectiveFormat === 'tdd'     && <><th>ID</th><th>Name</th><th>Requirement</th><th>Type</th><th>Input Data</th><th>Steps Summary</th><th>Expected Output</th><th>Pass Criteria</th></>}
                    </tr>
                  </thead>
                  <tbody>
                    {confirmedPageCases.map(tc => (
                      <tr key={tc.id}>
                        {effectiveFormat === 'normal' && <>
                          <td className="gen-id-cell">{tc.id}</td>
                          <td>{tc.title}</td>
                          <td><span className={`type-badge type-${(tc.type || '').toLowerCase()}`}>{tc.type}</span></td>
                          <td className="gen-cell-wrap">{tc.preconditions}</td>
                          <td className="gen-cell-wrap"><ol className="steps-list">{tc.steps.map((s, i) => <li key={i}>{s}</li>)}</ol></td>
                          <td className="gen-cell-wrap">{tc.testData}</td>
                          <td className="gen-cell-wrap">{tc.expectedResult}</td>
                        </>}
                        {effectiveFormat === 'gherkin' && <>
                          <td className="gen-id-cell">{tc.id}</td>
                          <td><span className={`type-badge type-${(tc.type || '').toLowerCase()}`}>{tc.type}</span></td>
                          <td>{tc.title}</td>
                          <td className="gen-cell-wrap gen-gherkin-steps"><GherkinSteps steps={tc.steps} /></td>
                        </>}
                        {effectiveFormat === 'tdd' && <>
                          <td className="gen-id-cell">{tc.id}</td>
                          <td>{tc.title}</td>
                          <td className="gen-cell-wrap">{tc.preconditions}</td>
                          <td><span className={`type-badge type-${(tc.type || '').toLowerCase()}`}>{tc.type}</span></td>
                          <td className="gen-cell-wrap">{tc.testData}</td>
                          <td className="gen-cell-wrap"><ol className="steps-list">{tc.steps.map((s, i) => <li key={i}>{s}</li>)}</ol></td>
                          <td className="gen-cell-wrap">{tc.expectedResult}</td>
                          <td className="gen-cell-wrap">{tc.passCriteria ?? ''}</td>
                        </>}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {confirmedTotalPages > 1 && (
                <div className="gen-pagination">
                  <button className="btn btn-outline btn-sm" onClick={() => setConfirmedPage(p => Math.max(1, p - 1))} disabled={confirmedPage === 1}>← Prev</button>
                  <span className="gen-page-info">Page {confirmedPage} of {confirmedTotalPages}</span>
                  <button className="btn btn-outline btn-sm" onClick={() => setConfirmedPage(p => Math.min(confirmedTotalPages, p + 1))} disabled={confirmedPage === confirmedTotalPages}>Next →</button>
                </div>
              )}

              {/* Submit panel */}
              <div className="gen-submit-panel">
                <h3 className="gen-section-title" style={{ marginBottom: 12 }}>Submit to Portal</h3>
                <div className="gen-submit-row">
                  <input className="form-input" placeholder="Session title" value={sessionTitle}
                    onChange={e => setSessionTitle(e.target.value)} style={{ flex: 1 }} />
                  <button className="btn btn-primary" onClick={submitToPortal} disabled={submitting || confirmedCases.length === 0}>
                    {submitting ? 'Submitting…' : `Submit ${confirmedCases.length} Case${confirmedCases.length !== 1 ? 's' : ''} for Review`}
                  </button>
                </div>
                {submitError && <div className="gen-error">{submitError}</div>}
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}
